import { AuthenticationAPI } from '@/library/entities/core/data/authentication.js';
import { COUNTRY_ID, COUNTRY_UAE_ID, ROOT_URL, CACHE_SETTINGS } from '@/data/constants.js'
import fetcher from '@/data/fetcher.js';
import { searchParamsFromObject } from '@/data/utils.js';

class PublicationAPI {
  static async getItem(id) {
    const url = `${ROOT_URL}/api/v1/publications/publications/${id}/`
    const res = await fetcher(url);
    const data = await res.json();
    return data
  }

}

class EditorialContentAPI {

  static kinds = {
    PRESSRELEASE_ANALYSIS: 1,
    TECHNICAL_ANALYSIS: 2,
    INSIGHT: 3,
    REPORT_ANALYSIS: 4,
  }

  static getKindSlug(kind) {
    switch (kind) {
      case this.kinds.PRESSRELEASE_ANALYSIS:
        return 'pressreleaseAnalysis'
      case this.kinds.TECHNICAL_ANALYSIS:
        return 'technicalAnalysis'
      case this.kinds.INSIGHT:
        return 'insight'
      case this.kinds.REPORT_ANALYSIS:
        return 'report'
      default:
        return ''
    }
  }

  static getKindClassName(kind) {
    switch (kind) {
      case this.kinds.PRESSRELEASE_ANALYSIS:
        return 'text-primary-500'
      case this.kinds.TECHNICAL_ANALYSIS:
        return 'text-tertiary-500'
      case this.kinds.INSIGHT:
        return 'text-quaternary-700'
      case this.kinds.REPORT_ANALYSIS:
        return 'text-zink-500'
      default:
        return ''
    }
  }

  static getKindShadowClassName(kind) {
    switch (kind) {
      case this.kinds.PRESSRELEASE_ANALYSIS:
        return 'shadow-xl shadow-primary-500'
      case this.kinds.TECHNICAL_ANALYSIS:
        return 'shadow-xl shadow-tertiary-500'
      case this.kinds.INSIGHT:
        return 'shadow-xl shadow-quaternary-700'
      case this.kinds.REPORT_ANALYSIS:
        return 'shadow-xl shadow-zink-500'
      default:
        return ''
    }
  }

  static async getList({ country_id = COUNTRY_UAE_ID, kind = '', instrument_id = '', offset = 0, limit = 10, query = '', instrument_sector_identifier = '', mini = false }) {
    let fields = ''
    if (mini) {
      fields = 'pk, slug, title, kind, published_at, instrument_identifier, instrument_slug, instrument_ticker, instrument_name, instrument_logotype, instrument_sector_name, instrument_sector_identifier, image_main_url, image_main_alt, video_main_url, preamble, summarized, country_id'
    }
    const params = searchParamsFromObject({
      country_id, instrument_id, offset, limit, query, kind, instrument_sector_identifier, fields
    }).toString();
    let url = `${ROOT_URL}/api/v1/publications/editorial-contents/search/?${params}`
    const res = await fetcher(url, {}, CACHE_SETTINGS.FETCH_EDITORIAL_CONTENT_LIST);
    const data = await res.json();
    return data;
  }

  static async getItem(slug, country_id = COUNTRY_UAE_ID) {
    const params = searchParamsFromObject({ slug, country_id }).toString();
    const url = `${ROOT_URL}/api/v1/publications/editorial-contents/search/?${params}`
    const res = await fetcher(url, {}, CACHE_SETTINGS.FETCH_EDITORIAL_CONTENT_DETAIL);
    const data = await res.json();
    const item = data[0]
    return item;
  }

  static async generateStaticParams(country_id = COUNTRY_UAE_ID) {
    if (country_id != COUNTRY_ID) return []
    const data = await this.getList({ country_id, limit: 20 })
    return data.map((item) => {
      return {
        slug: item.slug,
      }
    })
  }

  static async generateStaticParamsForInstrument(country_id = COUNTRY_UAE_ID) {
    if (country_id != COUNTRY_ID) return []
    const data = await this.getList({ country_id, limit: 50 })
    let instrumentEditorialParams = data.map((item) => {
      if (!item.instrument_identifier) return
      if (!item.instrument_ticker) return
      return {
        slug: item.slug,
        instrumentSlug: item.instrument_slug,
      }
    })
    instrumentEditorialParams = instrumentEditorialParams.filter((item) => item)
    return instrumentEditorialParams
  }

  static async generateStaticParamsForFilter() {
    return [
      { filters: 'page=1' },
      { filters: 'page=2' },
      { filters: 'page=3' },
      { filters: 'page=4' },
      { filters: 'page=5' },
    ]
  }
}

class EditorialContentEditorAPI {
  static async getList({ country = '', kind = '', instrument_id = '', offset = 0, limit = 20, query = '', instrument__sector_id = '' }) {
    const params = searchParamsFromObject({
      country, instrument_id, offset, limit, query, kind, instrument__sector_id,
    }).toString();
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-contents/?${params}`
    const res = await AuthenticationAPI.get(url);
    const data = await res.json();
    return data;
  }

  static async create({ payload }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-contents/`
    const res = await AuthenticationAPI.post(url, payload, null, 'form');
    const data = await res.json();
    return data;
  }

  static async getItem({ id }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-contents/${id}/`
    const res = await AuthenticationAPI.get(url);
    const data = await res.json();
    return data;
  }

  static async updateItem({ id, payload }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-contents/${id}/`
    const res = await AuthenticationAPI.patch(url, payload, null, 'form');
    const data = await res.json();
    return data;
  }

  static async generateStitchedVideo({ id, payload }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-contents/${id}/generate-video/stitched/`
    const res = await AuthenticationAPI.post(url, payload, null, 'form');
    const data = await res.json();
    return data;
  }
}

class EditorialContentVideoEditorAPI {
  static async getList({ offset = 0, limit = 20, query = '' }) {
    const params = searchParamsFromObject({ offset, limit, query }).toString();
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-content-videos/?${params}`
    const res = await AuthenticationAPI.get(url);
    const data = await res.json();
    return data;
  }

  static async getItem({ id }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-content-videos/${id}/`
    const res = await AuthenticationAPI.get(url);
    const data = await res.json();
    return data;
  }

  static async updateItem({ id, payload }) {
    let url = `${ROOT_URL}/api/v1/publications/editor/editorial-content-videos/${id}/`
    const res = await AuthenticationAPI.patch(url, payload, null, 'form');
    const data = await res.json();
    return data;
  }

  static getStatusString(status) {
    if (!status) return 'N/A'
    switch (status) {
      case 1:
        return 'Draft'
      case 2:
        return 'Generating'
      case 3:
        return 'Completed'
      case 4:
        return 'Failed'
      default:
        return 'N/A'
    }
  }

  static getVideoAssetKindString(kind) {
    if (!kind) return 'N/A'
    switch (kind) {
      case 1:
        return 'Video Clip'
      case 2:
        return 'Video Main'
      case 3:
        return 'Subtitle'
      case 4:
        return 'Audio Narration'
      case 5:
        return 'Thumbnail'
      default:
        return 'N/A'
    }
  }
}

export {
  EditorialContentAPI,
  EditorialContentEditorAPI,
  EditorialContentVideoEditorAPI,
  PublicationAPI
}